/** The case editor's form: from a saved case, to the request body, and what changed since it opened. */

import { CaseCreateIn as CaseCreateSchema, MetricIn as MetricSchema } from '@/api/generated/schemas';
import type { CaseCreateIn, CaseMetric, CaseOut, CaseStatus, CaseType, CaseUpdateIn, MetricIn, Provenance } from '@/api/types/cases';
import { fieldErrors, oneOf, type FieldErrors } from '@/api/validate';

export interface MetricRow {
  key: string;
  label: string;
  value: string;
  before: string;
  after: string;
  unit: string;
  provenance: Provenance;
}

export interface CaseForm {
  type: CaseType;
  client_name: string;
  aliases: string[];
  anonymized_label: string;
  industry_id: string;
  country: string;
  status: CaseStatus;
  situation: string;
  objective: string;
  approach: string;
  achievements: string;
  metrics: MetricRow[];
}

let rowCounter = 0;
const text = (v: string | number | null | undefined) => (v === null || v === undefined ? '' : String(v));

export function metricRow(metric?: CaseMetric): MetricRow {
  rowCounter += 1;
  return {
    key: `m${rowCounter}`,
    label: metric?.label ?? '',
    value: text(metric?.value),
    before: text(metric?.before),
    after: text(metric?.after),
    unit: text(metric?.unit),
    provenance: oneOf(MetricSchema.shape.provenance, metric?.provenance, 'client_measured'),
  };
}

export function emptyCaseForm(): CaseForm {
  return {
    type: 'client',
    client_name: '',
    aliases: [],
    anonymized_label: '',
    industry_id: '',
    country: '',
    status: 'live',
    situation: '',
    objective: '',
    approach: '',
    achievements: '',
    metrics: [],
  };
}

export function caseFormFrom(saved: CaseOut): CaseForm {
  return {
    type: oneOf(CaseCreateSchema.shape.type, saved.type, 'client'),
    client_name: saved.client_name ?? '',
    aliases: [...saved.aliases],
    anonymized_label: saved.anonymized_label,
    industry_id: saved.industry?.id ?? '',
    country: saved.country ?? '',
    status: oneOf(CaseCreateSchema.shape.status, saved.status, 'live'),
    situation: saved.situation,
    objective: saved.objective,
    approach: saved.approach,
    achievements: saved.achievements,
    metrics: [...saved.metrics].sort((a, b) => a.position - b.position).map((m) => metricRow(m)),
  };
}

const orNull = (v: string) => (v.trim() === '' ? null : v.trim());

function metricsBody(rows: MetricRow[]): MetricIn[] {
  return rows
    .filter((r) => r.label.trim() !== '')
    .map((r) => ({
      label: r.label.trim(),
      value: orNull(r.value),
      before: orNull(r.before),
      after: orNull(r.after),
      unit: orNull(r.unit),
      provenance: r.provenance,
    }));
}

export function caseBody(form: CaseForm): CaseCreateIn {
  return {
    type: form.type,
    client_name: orNull(form.client_name),
    aliases: form.aliases,
    anonymized_label: form.anonymized_label.trim(),
    industry_id: orNull(form.industry_id),
    country: orNull(form.country),
    status: form.status,
    situation: form.situation.trim(),
    objective: form.objective.trim(),
    approach: form.approach.trim(),
    achievements: form.achievements.trim(),
    metrics: metricsBody(form.metrics),
  };
}

/** Only the fields that differ from the saved case: an untouched text must not clear the share confirmation. */
export function changedFields(saved: CaseForm, form: CaseForm): CaseUpdateIn {
  const before = new Map(Object.entries(caseBody(saved)));
  return Object.fromEntries(
    Object.entries(caseBody(form)).filter(([key, value]) => JSON.stringify(before.get(key)) !== JSON.stringify(value)),
  );
}

const LABEL_PROBLEM = 'Give the case an anonymized label, such as "Tier 1 bank".';

/** What a person types; the industry id and the country code come from the server's own lists. */
const TYPED = CaseCreateSchema.pick({
  type: true,
  status: true,
  client_name: true,
  aliases: true,
  anonymized_label: true,
  situation: true,
  objective: true,
  approach: true,
  achievements: true,
});

/**
 * What the API would refuse, by field. A metric's problems are keyed by its row in the editor (`metrics.<row>.<column>`);
 * a row without a label is left out when saving, so it is not checked.
 */
export function caseErrors(form: CaseForm): FieldErrors {
  const { metrics, industry_id: _industry, country: _country, ...typed } = form;
  const errors = fieldErrors(TYPED, typed, { anonymized_label: LABEL_PROBLEM });
  metrics.forEach((row, i) => {
    if (row.label.trim() === '') return;
    const { key: _key, ...metric } = row;
    Object.entries(fieldErrors(MetricSchema, metric)).forEach(([path, message]) => (errors[`metrics.${i}.${path}`] = message));
  });
  return errors;
}

const FIELD_NAMES: Record<string, string> = {
  type: 'Type',
  status: 'Status',
  client_name: 'Client name',
  aliases: 'Aliases',
  situation: 'Situation',
  objective: 'Objective',
  approach: 'Approach',
  achievements: 'Achievements',
};

/** A problem from `caseErrors` with the field it is about, for a list away from the fields: "Metric 2 value: …". */
export function caseErrorText(path: string, message: string): string {
  const metric = /^metrics\.(\d+)\.(\w+)$/.exec(path);
  const alias = /^aliases\.(\d+)$/.exec(path);
  const name = metric ? `Metric ${Number(metric[1]) + 1} ${metric[2]}` : alias ? `Alias ${Number(alias[1]) + 1}` : (FIELD_NAMES[path] ?? path);
  return `${name}: ${message}`;
}

/** Why the form cannot be saved yet, or null. */
export function caseProblem(form: CaseForm): string | null {
  return caseErrors(form).anonymized_label ?? null;
}

/** The names the anonymized version replaces. */
export function namesOf(form: CaseForm): string[] {
  return [form.client_name, ...form.aliases];
}
